<template>
  <div class="doctor-calendar">
    <div class="doctor-calendar__workspace">
      <aside class="doctor-calendar__sidebar" aria-label="Navigasi kalender dokter">
        <section class="doctor-calendar__summary">
          <span class="doctor-calendar__eyebrow">Ringkasan jadwal</span>
          <h3>Aktivitas dokter</h3>
          <div class="doctor-calendar__metric">
            <span class="doctor-calendar__metric-icon"><i class="far fa-calendar-check"></i></span>
            <span><strong>{{ appointmentCount }}</strong><small>Perjanjian</small></span>
          </div>
          <div class="doctor-calendar__metric">
            <span class="doctor-calendar__metric-icon doctor-calendar__metric-icon--navy"><i class="far fa-clock"></i></span>
            <span><strong>{{ scheduleCount }}</strong><small>Jadwal lainnya</small></span>
          </div>
        </section>

        <section class="doctor-calendar__legend" aria-label="Keterangan status perjanjian">
          <span class="doctor-calendar__eyebrow">Keterangan</span>
          <h3>Status perjanjian</h3>
          <div class="doctor-calendar__status-list">
            <div
              v-for="status in statusLegend"
              :key="status.name"
              class="doctor-calendar__status"
              :class="`doctor-calendar__status--${status.tone}`"
            >
              <i :class="status.icon" aria-hidden="true"></i>
              <span>{{ status.name }}</span>
            </div>
          </div>
        </section>
      </aside>

      <section class="doctor-calendar__main">
        <header class="doctor-calendar__toolbar">
          <div>
            <span class="doctor-calendar__eyebrow">Jadwal praktik</span>
            <h2 v-if="$refs.calendar">{{ $refs.calendar.title }}</h2>
            <h2 v-else>Kalender dokter</h2>
          </div>
          <div class="doctor-calendar__actions">
            <button type="button" class="doctor-calendar__today" @click="setToday">
              <i class="far fa-calendar-check"></i>
              Hari ini
            </button>
            <div class="doctor-calendar__nav">
              <button type="button" aria-label="Periode sebelumnya" @click="prev"><i class="fas fa-chevron-left"></i></button>
              <button type="button" aria-label="Periode berikutnya" @click="next"><i class="fas fa-chevron-right"></i></button>
            </div>
            <v-menu bottom right content-class="doctor-calendar__view-menu">
              <template v-slot:activator="{ on, attrs }">
                <button type="button" class="doctor-calendar__view" v-bind="attrs" v-on="on">
                  {{ typeToLabel[type] }}
                  <i class="fas fa-chevron-down"></i>
                </button>
              </template>
              <v-list dense>
                <v-list-item @click="type = 'day'"><v-list-item-title>Per Hari</v-list-item-title></v-list-item>
                <v-list-item @click="type = 'week'"><v-list-item-title>Per Minggu</v-list-item-title></v-list-item>
                <v-list-item @click="type = 'month'"><v-list-item-title>Per Bulan</v-list-item-title></v-list-item>
                <v-list-item @click="type = '4day'"><v-list-item-title>Per 4 Hari</v-list-item-title></v-list-item>
              </v-list>
            </v-menu>
          </div>
        </header>

        <div class="doctor-calendar__body">
          <v-calendar
            ref="calendar"
            v-model="value"
            locale="id"
            :weekdays="weekday"
            :type="type"
            :events="events"
            :event-overlap-threshold="30"
            :event-color="getEventColor"
            @click:event="showEvent"
            @click:more="viewDay"
            @click:date="viewDay"
            v-if="calendarLoad"
          ></v-calendar>
        </div>
      </section>
    </div>

    <div
      v-if="selectedOpen"
      class="doctor-calendar__event-menu"
      :style="{ left: `${popoverX}px`, top: `${popoverY}px` }"
      role="dialog"
      aria-label="Detail agenda perjanjian"
    >
      <v-card class="doctor-calendar__event-card" flat>
        <div class="doctor-calendar__event-head" :style="{ borderColor: selectedEvent.color }">
          <div>
            <span>Detail agenda</span>
            <strong>{{ selectedEvent.name || 'Perjanjian' }}</strong>
          </div>
          <button type="button" aria-label="Tutup detail agenda" @click="selectedOpen = false">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <v-card-text class="doctor-calendar__event-content">
          <template v-if="selectedEvent.type == 'Perjanjian'">
            <div class="doctor-calendar__event-row doctor-calendar__event-row--wide">
              <i class="far fa-calendar-alt"></i>
              <span><small>Jadwal</small><strong>{{ selectedEvent.dayName }}</strong><b>{{ selectedEvent.startLabel }} s/d {{ selectedEvent.endLabel }}</b></span>
            </div>
            <div class="doctor-calendar__event-grid">
              <div class="doctor-calendar__event-row"><i class="fas fa-user-md"></i><span><small>Dokter</small><strong>{{ selectedEvent.doctor_name || '-' }}</strong></span></div>
              <div class="doctor-calendar__event-row"><i class="far fa-question-circle"></i><span><small>Status</small><strong>{{ selectedEvent.status_name || '-' }}</strong></span></div>
            </div>
            <div class="doctor-calendar__event-row"><i class="fas fa-hand-holding-medical"></i><span><small>Tindakan</small><strong>{{ selectedEvent.action_type_name || '-' }}</strong></span></div>
            <div v-if="selectedEvent.notes" class="doctor-calendar__event-note"><small><i class="far fa-sticky-note"></i> Catatan</small><p>{{ selectedEvent.notes }}</p></div>
          </template>
          <p v-else>{{ selectedEvent.details }}</p>
        </v-card-text>
        <v-card-actions v-if="selectedEvent.type == 'Perjanjian' && selectedEvent.id" class="doctor-calendar__event-actions">
          <button type="button" @click="goToAppointmentDetail(selectedEvent.id)">Lihat detail perjanjian <i class="fas fa-arrow-right"></i></button>
        </v-card-actions>
      </v-card>
    </div>
  </div>
</template>

<script>
import module from "@/core/modules/CrudModule.js";

export default {
  props: {
    isMyAppointment: Boolean,
    currentDoctor: Object,
  },

  data: () => ({
    // Calendar
    type: "month",
    typeToLabel: {
      month: "Per Bulan",
      week: "Per Minggu",
      day: "Per Hari",
      "4day": "Per 4 Hari",
    },
    weekday: [0, 1, 2, 3, 4, 5, 6],
    value: "",
    events: [],
    colors: [
      "red",
      "red lighten-5",
      "red lighten-4",
      "red lighten-3",
      "red lighten-2",
      "red lighten-1",
      "red darken-1",
      "red darken-2",
      "red darken-3",
      "red darken-4",
      "red accent-1",
      "red accent-2",
      "red accent-3",
      "red accent-4",
      "pink",
      "pink lighten-5",
      "pink lighten-4",
      "pink lighten-3",
      "pink lighten-2",
      "pink lighten-1",
      "pink darken-1",
      "pink darken-2",
      "pink darken-3",
      "pink darken-4",
      "pink accent-1",
      "pink accent-2",
      "pink accent-3",
      "pink accent-4",
      "purple",
      "purple lighten-5",
      "purple lighten-4",
      "purple lighten-3",
      "purple lighten-2",
      "purple lighten-1",
      "purple darken-1",
      "purple darken-2",
      "purple darken-3",
      "purple darken-4",
      "purple accent-1",
      "purple accent-2",
      "purple accent-3",
      "purple accent-4",
      "deep-purple",
      "deep-purple lighten-5",
      "deep-purple lighten-4",
      "deep-purple lighten-3",
      "deep-purple lighten-2",
      "deep-purple lighten-1",
      "deep-purple darken-1",
      "deep-purple darken-2",
      "deep-purple darken-3",
      "deep-purple darken-4",
      "deep-purple accent-1",
      "deep-purple accent-2",
      "deep-purple accent-3",
      "deep-purple accent-4",
      "indigo",
      "indigo lighten-5",
      "indigo lighten-4",
      "indigo lighten-3",
      "indigo lighten-2",
      "indigo lighten-1",
      "indigo darken-1",
      "indigo darken-2",
      "indigo darken-3",
      "indigo darken-4",
      "indigo accent-1",
      "indigo accent-2",
      "indigo accent-3",
      "indigo accent-4",
      "blue",
      "blue lighten-5",
      "blue lighten-4",
      "blue lighten-3",
      "blue lighten-2",
      "blue lighten-1",
      "blue darken-1",
      "blue darken-2",
      "blue darken-3",
      "blue darken-4",
      "blue accent-1",
      "blue accent-2",
      "blue accent-3",
      "blue accent-4",
      "light-blue",
      "light-blue lighten-5",
      "light-blue lighten-4",
      "light-blue lighten-3",
      "light-blue lighten-2",
      "light-blue lighten-1",
      "light-blue darken-1",
      "light-blue darken-2",
      "light-blue darken-3",
      "light-blue darken-4",
      "light-blue accent-1",
      "light-blue accent-2",
      "light-blue accent-3",
      "light-blue accent-4",
      "cyan",
      "cyan lighten-5",
      "cyan lighten-4",
      "cyan lighten-3",
      "cyan lighten-2",
      "cyan lighten-1",
      "cyan darken-1",
      "cyan darken-2",
      "cyan darken-3",
      "cyan darken-4",
      "cyan accent-1",
      "cyan accent-2",
      "cyan accent-3",
      "cyan accent-4",
      "teal",
      "teal lighten-5",
      "teal lighten-4",
      "teal lighten-3",
      "teal lighten-2",
      "teal lighten-1",
      "teal darken-1",
      "teal darken-2",
      "teal darken-3",
      "teal darken-4",
      "teal accent-1",
      "teal accent-2",
      "teal accent-3",
      "teal accent-4",
      "green",
      "green lighten-5",
      "green lighten-4",
      "green lighten-3",
      "green lighten-2",
      "green lighten-1",
      "green darken-1",
      "green darken-2",
      "green darken-3",
      "green darken-4",
      "green accent-1",
      "green accent-2",
      "green accent-3",
      "green accent-4",
      "light-green",
      "light-green lighten-5",
      "light-green lighten-4",
      "light-green lighten-3",
      "light-green lighten-2",
      "light-green lighten-1",
      "light-green darken-1",
      "light-green darken-2",
      "light-green darken-3",
      "light-green darken-4",
      "light-green accent-1",
      "light-green accent-2",
      "light-green accent-3",
      "light-green accent-4",
    ],

    // colors: ["bg-filed", "bt-conf-wait", "bt-conf", "bt-handled", "bg-danger"],
    // Event
    selectedEvent: {},
    selectedElement: null,
    selectedOpen: false,
    popoverX: 0,
    popoverY: 0,
    calendarLoad: true,
    statusLegend: [
      { name: "Diajukan", tone: "primary", icon: "far fa-paper-plane" },
      { name: "Menunggu", tone: "warning", icon: "fas fa-hourglass-half" },
      { name: "Terkonfirmasi", tone: "success", icon: "far fa-check-circle" },
      { name: "Ditangani", tone: "info", icon: "fas fa-check-double" },
      { name: "Batal", tone: "danger", icon: "far fa-times-circle" },
      { name: "Selesai / Pulang", tone: "success", icon: "far fa-check-circle" },
    ],
  }),

  methods: {
    async getDoctorEvents() {
      const events = [];
      let doctor_id = this.isMyAppointment
        ? this.currentDoctor.id
        : this.$route.params.id;
      const doctorEvents = await module.list("doctors/get-events/" + doctor_id);
      const eventCount = doctorEvents.length;
      for (let i = 0; i < eventCount; i++) {
        events.push({
          id: doctorEvents[i].id,
          name: doctorEvents[i].name,
          type: doctorEvents[i].type,
          start: doctorEvents[i].start_time,
          end: doctorEvents[i].end_time,
          doctor_name: doctorEvents[i].doctor_name,
          notes: doctorEvents[i].notes,
          status_name: doctorEvents[i].appointment_status_name,
          action_type_name: doctorEvents[i].action_type_name,
          color: this.getBrandEventColor(doctorEvents[i], i),
          timed: true,
        });
      }
      this.events = await events;
      this.calendarLoad = true;
      console.info("events", this.events);
    },

    showEvent({ nativeEvent, event }) {
      const open = () => {
        this.selectedEvent = event;
        if (event.type == "Perjanjian") {
          let dayName = new Date(event.start).toLocaleDateString("id-ID", {
            weekday: "long",
          });
          let start = new Date(event.start).toLocaleTimeString("id-ID", {
            hour: "2-digit",
            minute: "2-digit",
          });
          let end = new Date(event.end).toLocaleTimeString("id-ID", {
            hour: "2-digit",
            minute: "2-digit",
          });
          this.selectedEvent.details = `${event.name} Pada ${dayName} ${start} s/d ${end} dengan ${event.doctor_name}`;
          this.selectedEvent.notes = `Catatan: ${event.notes}`;
          this.selectedEvent.status = `Status: ${event.status_name}`;
          this.selectedEvent.action = `Tindakan: ${event.action_type_name}`;
          this.selectedEvent.dayName = dayName;
          this.selectedEvent.startLabel = start;
          this.selectedEvent.endLabel = end;
        } else {
          let fullDate = new Date(event.start).toLocaleDateString("id-ID", {
            weekday: "long",
            day: "numeric",
            month: "numeric",
            year: "numeric",
          });
          this.selectedEvent.details = `${event.name} Pada ${fullDate}`;
        }
        this.selectedElement = nativeEvent.currentTarget || nativeEvent.target;
        const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
        const panelWidth = Math.min(346, viewportWidth - 32);
        const panelHeight = event.type === "Perjanjian" ? 470 : 220;
        this.popoverX = Math.max(16, Math.min(nativeEvent.clientX + 10, viewportWidth - panelWidth - 16));
        this.popoverY = Math.max(16, Math.min(nativeEvent.clientY + 10, viewportHeight - panelHeight - 16));
        setTimeout(() => {
          this.selectedOpen = true;
        }, 30);
      };
      if (this.selectedOpen) {
        this.selectedOpen = false;
        setTimeout(open, 30);
      } else {
        open();
      }
      if (nativeEvent && nativeEvent.stopPropagation) nativeEvent.stopPropagation();
    },

    viewDay({ date }) {
      console.log("date", date);
      this.value = date;
      this.type = "day";
    },

    setToday() {
      this.value = "";
    },

    prev() {
      this.$refs.calendar.prev();
    },

    next() {
      this.$refs.calendar.next();
    },

    getEventColor(event) {
      return event.color;
    },

    getBrandEventColor(event, index) {
      const status = (event.appointment_status_name || "").toLowerCase();
      if (status.includes("batal")) return "#DC3545";
      if (status.includes("menunggu")) return "#B45309";
      if (status.includes("konfirmasi")) return "var(--skin-primary)";
      if (status.includes("ditangani") || status.includes("selesai")) return "#245C91";
      if (event.type !== "Perjanjian") return "var(--skin-primary)";
      return ["var(--skin-deep)", "var(--skin-primary)", "var(--skin-secondary)"][index % 3];
    },

    goToAppointmentDetail(id) {
      this.selectedOpen = false;
      this.$router.push(`/appointment/detail/${id}`);
    },

    random(a, b) {
      return Math.floor((b - a + 1) * Math.random()) + a;
    },
  },
  computed: {
    appointmentCount() {
      return this.events.filter((event) => event.type === "Perjanjian").length;
    },
    scheduleCount() {
      return this.events.filter((event) => event.type !== "Perjanjian").length;
    },
  },
  mounted() {
    this.getDoctorEvents();
  },
};
</script>

<style lang="scss">
@import "~vuetify/dist/vuetify.css";
.theme--light.v-application {
  background: transparent !important;
}
.v-application code {
  box-shadow: none !important;
}

.doctor-calendar__main .v-event,
.doctor-calendar__main .v-event * {
  color: #FFFFFF !important;
  cursor: pointer !important;
}

.doctor-calendar__main .v-event:hover {
  filter: brightness(1.08);
  transform: translateY(-1px);
}

.doctor-calendar__legend {
  padding: 16px;
  background: #FFFFFF;
  border: 1px solid #E1E9F2;
  border-radius: 18px;
  box-shadow: 0 10px 28px rgba(var(--skin-deep-rgb), 0.05);
}

.doctor-calendar__legend h3 {
  margin: 2px 0 12px;
  color: #1A2433;
  font-size: 15px;
  font-weight: 800;
}

.doctor-calendar__status-list {
  display: grid;
  gap: 8px;
}

.doctor-calendar__status {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 38px;
  padding: 8px 12px;
  border: 0 !important;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 700;
}

.doctor-calendar__status i { width: 16px; text-align: center; }
.doctor-calendar__status--primary { color: #1871CF; background: #E7F1FC; }
.doctor-calendar__status--warning { color: #E56614; background: #FFF0E4; }
.doctor-calendar__status--success { color: #15965A; background: #E7F7EE; }
.doctor-calendar__status--info { color: #6F50C9; background: #F0EBFB; }
.doctor-calendar__status--danger { color: #DF3D3D; background: #FDECEC; }

.doctor-calendar__event-menu {
  position: fixed;
  z-index: 1200;
  border-radius: 16px !important;
}
.doctor-calendar__event-card {
  width: min(346px, calc(100vw - 32px));
  overflow: hidden;
  border: 1px solid #DFE8F1 !important;
  border-radius: 16px !important;
  box-shadow: 0 20px 50px rgba(var(--skin-deep-rgb), 0.18) !important;
}
.doctor-calendar__event-head {
  display: flex !important;
  align-items: flex-start;
  justify-content: space-between;
  padding: 18px 18px 14px !important;
  color: #1A2433 !important;
  background: #FFFFFF !important;
  border-left: 5px solid #246AA3;
}
.doctor-calendar__event-head span,
.doctor-calendar__event-head strong { display: block; color: #1A2433 !important; }
.doctor-calendar__event-head span { color: #8594A6 !important; font-size: 10px !important; font-weight: 800; letter-spacing: .08em; text-transform: uppercase; }
.doctor-calendar__event-head strong { margin-top: 3px; font-size: 16px !important; }
.doctor-calendar__event-head button { width: 32px; height: 32px; color: var(--skin-primary); background: #EDF5F8; border: 0; border-radius: 10px; }
.doctor-calendar__event-content { padding: 0 18px 10px !important; color: #314057 !important; }
.doctor-calendar__event-row { display: flex; align-items: flex-start; gap: 9px; padding: 10px 0; }
.doctor-calendar__event-row--wide { border-bottom: 1px solid #E8EEF4; }
.doctor-calendar__event-row > i { display: grid; place-items: center; width: 30px; height: 30px; color: var(--skin-primary); background: var(--skin-soft); border-radius: 9px; }
.doctor-calendar__event-row span { min-width: 0; }
.doctor-calendar__event-row small,
.doctor-calendar__event-row strong,
.doctor-calendar__event-row b { display: block; }
.doctor-calendar__event-row small { color: #8A99AA; font-size: 10px; font-weight: 700; }
.doctor-calendar__event-row strong { color: #314057; font-size: 12px; font-weight: 800; }
.doctor-calendar__event-row b { color: #596B80; font-size: 10px; }
.doctor-calendar__event-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; border-bottom: 1px solid #E8EEF4; }
.doctor-calendar__event-note { margin-top: 8px; padding: 11px 12px; background: #F7F9FB; border: 1px solid #E3EAF1; border-radius: 11px; }
.doctor-calendar__event-note small { color: #8190A2; font-size: 10px; font-weight: 800; text-transform: uppercase; }
.doctor-calendar__event-note p { margin: 5px 0 0 !important; color: #53657A !important; font-size: 11px !important; }
.doctor-calendar__event-actions { padding: 0 18px 18px !important; }
.doctor-calendar__event-actions button { width: 100%; padding: 10px 14px; color: #147E78; background: #EAF6F5; border: 0; border-radius: 10px; font-size: 11px; font-weight: 800; }

@media (max-width: 991px) {
  .doctor-calendar__sidebar { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}

@media (max-width: 640px) {
  .doctor-calendar__sidebar { grid-template-columns: 1fr; }
  .doctor-calendar__event-grid { grid-template-columns: 1fr; gap: 0; }
}
</style>
